import React, { useEffect, useImperativeHandle, useRef } from "react";
import { DeviceEnum } from "../../types/context-detection";
import { DeviceScrollEnum, ISuggestionColumnsProps } from "../../types/suggestions";
import { IBrick } from "../../types/suggestion-response";
import { ISearchInfo } from "../../types/search-info";
import {
    IMappedContent,
    IMappedProducts,
    IMappedTopSearchQuery,
    IPromotion
} from "../../types/suggestion-items";
import { TelemetryContentEnum } from "../../types/telemetry";

import { useL10nContext } from "../common/contexts";
import { useProductRenderAmount, cropProductLists } from "./product-render-amount";
import ContentLink from "../suggestion-items/content-link";
import mapProductFinderL10nToPromotion from "../suggestion-items/promotion/finder-l10n-to-promotion";
import ProductTiles from "../suggestion-items/product-tiles/product-tiles";
import Promotion from "../suggestion-items/promotion/promotion";
import TopSearchQuery from "../suggestion-items/top-search-query";

const SuggestionColumns = (props: ISuggestionColumnsProps) => {
    const navigationRefs = useRef<(HTMLElement | null)[]>([]);

    const {
        decodeProductFinderL10n,
        articlesHeadline,
        contentHeadline,
        seeAllResults,
        topSearchQueriesHeadline
    } = useL10nContext();
    const pfL10n = decodeProductFinderL10n();
    const promotions: Record<string, IPromotion> = mapProductFinderL10nToPromotion(pfL10n);

    let columnLength = 0;
    const mappedFeaturePromotion =
        props.isMobileShop || !props.featurePromotion?.trim()
            ? null
            : promotions[props.featurePromotion.toLowerCase()] || null;
    const showPromotion = mappedFeaturePromotion !== null;
    columnLength = showPromotion ? 1 : 0;

    const showTopSearchQueries = props.topSearchQueries.length > 0;
    const showContentLinks = props.contents.length > 0;
    const rendersMultipleContainers = showTopSearchQueries || showContentLinks;

    const visibleProducts = useProductRenderAmount(
        props.view,
        props.deviceType,
        "column",
        rendersMultipleContainers
    );

    // Map suggestion items in the order in which they will be rendered.
    const mappedTopSearchQueries = mapTopSearchQueries(columnLength);
    columnLength += mappedTopSearchQueries.length;

    const mappedContentLinks = mapContentLinks(columnLength);
    columnLength += mappedContentLinks.length;

    const mappedProducts = mapProducts(columnLength);
    const productsLength = mappedProducts.articles.length + mappedProducts.flexibleSets.length;
    const showProducts = productsLength > 0;
    columnLength += productsLength + (showProducts ? (productsLength >= 2 ? 2 : 1) : 0);

    const deviceScrollClass = updateDefaultScrollClass();

    const searchInfo: ISearchInfo = {
        pageViewLogDataId: props.pageViewLogDataId,
        culture: props.culture,
        portal: props.portal,
        searchTerm: props.searchTerm,
        trackingEndpoint: props.trackingEndpoint
    };

    useEffect(() => {
        navigationRefs.current = navigationRefs.current.filter(Boolean);
    }, [props, mappedProducts]);

    const setRef = (key: number, element: HTMLElement | null) => {
        navigationRefs.current[key] = element;
    };

    useImperativeHandle(props.navigationRef, () => ({
        getNavigationElements: () => navigationRefs.current
    }));

    function updateDefaultScrollClass(): DeviceScrollEnum {
        // update scroll for searchbox wide on desktop devices
        if (props.view === "wide" && props.deviceType === DeviceEnum.Desktop) {
            // don't add scrolling for cases with no promotion tile, 4 products + 1 button or less
            // and less than 14 left suggestions items (+ 2 headlines)
            const countLeftItems = mappedContentLinks.length + mappedTopSearchQueries.length;
            if (!showPromotion && mappedProducts.clickableItemsCount <= 5 && countLeftItems <= 16) {
                // avoids unnecessary whitespace on shorter suggestion lists
                return DeviceScrollEnum.None;
            }

            // enable scrolling of left suggestions with narrow viewport height
            const innerHeight = window.innerHeight;
            const mktHeaderHeight = 50;
            const leftSuggestionContentMaxHeight = 918;
            const windowMinHeight = mktHeaderHeight + leftSuggestionContentMaxHeight;
            if (innerHeight - windowMinHeight < 0) {
                return DeviceScrollEnum.Desktop;
            }
        }

        return props.deviceScrollClass;
    }

    function mapProducts(length: number): IMappedProducts {
        let productsLength = props.articles.length + props.flexibleSets.length;
        if (productsLength === 0) return { articles: [], flexibleSets: [], clickableItemsCount: 0 };

        // crop lists according to device type/size
        cropProductLists(props.articles, props.flexibleSets, visibleProducts);
        productsLength = props.articles.length + props.flexibleSets.length;

        let navIndex = length;
        const result: IMappedProducts = {
            headline: {
                title: articlesHeadline,
                type: TelemetryContentEnum.Articles,
                navIndex: navIndex++
            },
            articles: [
                ...props.articles.map((article) => {
                    return {
                        isHeadline: false,
                        title: article.title,
                        image: article.image,
                        price: article.price,
                        target: article.url,
                        shortLink: article.shortLink,
                        type: TelemetryContentEnum.Articles,
                        navIndex: navIndex++,
                        availableColorsCount: article.availableColorsCount,
                        availableModelsCount: article.availableModelsCount
                    };
                })
            ],
            flexibleSets: [
                ...props.flexibleSets.map((set) => {
                    return {
                        articleCount: set.articleCount,
                        brickItems: [
                            ...set.brickItems.map((b) => {
                                return {
                                    articleName: b.articleName,
                                    articleImage: b.articleImage
                                } as IBrick;
                            })
                        ],
                        flexibleSetImage: set.flexibleSetImage,
                        isHeadline: false,
                        navIndex: navIndex++,
                        price: set.price,
                        shortLink: set.shortLink,
                        title: set.title,
                        type: TelemetryContentEnum.FlexibleSet,
                        url: set.url
                    };
                })
            ],
            clickableItemsCount: productsLength
        };

        if (productsLength >= 2) {
            result.showMoreButton = {
                isHeadline: false,
                title: seeAllResults,
                image: "",
                target: `${props.searchTarget}?query=${encodeURIComponent(props.searchTerm)}`,
                shortLink: "",
                type: TelemetryContentEnum.ShowAllResults,
                navIndex: navIndex++,
                availableColorsCount: 0,
                availableModelsCount: 0
            };
            result.clickableItemsCount++;
        }

        return result;
    }

    function mapTopSearchQueries(length: number): IMappedTopSearchQuery[] {
        if (!props.topSearchQueries.length) return [];

        let navIndex = length;
        const result: IMappedTopSearchQuery[] = [
            {
                value: topSearchQueriesHeadline,
                isHeadline: true,
                target: "",
                type: TelemetryContentEnum.TopSearchQuery,
                navIndex: navIndex++
            },
            ...props.topSearchQueries.map((query, index) => ({
                value: query,
                isHeadline: false,
                target: `${props.searchTarget}?query=${encodeURIComponent(query)}`,
                type: TelemetryContentEnum.TopSearchQuery,
                navIndex: navIndex++,
                anchorIndex: index
            }))
        ];

        return result;
    }

    function mapContentLinks(length: number): IMappedContent[] {
        if (!props.contents.length) return [];

        let navIndex = length;
        const result: IMappedContent[] = [
            {
                isHeadline: true,
                title: contentHeadline,
                target: "",
                shortLink: "",
                type: TelemetryContentEnum.Content,
                navIndex: navIndex++
            },
            ...props.contents.map((content, index) => ({
                isHeadline: false,
                title: content.title,
                target: content.url,
                shortLink: content.shortLink,
                type: TelemetryContentEnum.Content,
                navIndex: navIndex++,
                anchorIndex: index
            }))
        ];

        return result;
    }

    return (
        <div className={`suggestions-container multiple-columns ${deviceScrollClass} columns`}>
            {rendersMultipleContainers && (
                <ul className="suggestions-list-container">
                    {showPromotion && (
                        <Promotion
                            key={mappedFeaturePromotion.navIndex}
                            setRef={(el) => setRef(mappedFeaturePromotion.navIndex, el)}
                            promotion={mappedFeaturePromotion}
                            style="stretchy"
                            searchInfo={searchInfo}
                        />
                    )}
                    {mappedTopSearchQueries.map((query, index) => (
                        <TopSearchQuery
                            key={index}
                            setRef={(el) => setRef(query.navIndex, el)}
                            topSearchQuery={query}
                            searchInfo={searchInfo}
                        />
                    ))}
                    {mappedContentLinks.map((contentLink, index) => (
                        <ContentLink
                            key={index}
                            setRef={(el) => setRef(contentLink.navIndex, el)}
                            contentLink={contentLink}
                            searchInfo={searchInfo}
                        />
                    ))}
                </ul>
            )}

            {!rendersMultipleContainers && showPromotion && (
                <Promotion
                    key={mappedFeaturePromotion.navIndex}
                    setRef={(el) => setRef(mappedFeaturePromotion.navIndex, el)}
                    promotion={mappedFeaturePromotion}
                    style="stretchy"
                    searchInfo={searchInfo}
                />
            )}

            {showProducts && (
                <ProductTiles
                    products={mappedProducts}
                    setRef={setRef}
                    customerType={props.customerType}
                    hasLeftListItems={rendersMultipleContainers}
                    isMobileShop={props.isMobileShop}
                    priceMandantSettings={props.priceMandantSettings}
                    priceMandantSettingsV2={props.priceMandantSettingsV2}
                    searchInfo={searchInfo}
                />
            )}
        </div>
    );
};

export default SuggestionColumns;
